import { cn } from "../../../utils/cn.js";
import { cva } from "class-variance-authority";
import * as React from "react";
import { jsx } from "react/jsx-runtime";
//#region src/components/EmptyState/EmptyState.tsx
const emptyStateRootVariants = cva("flex flex-col items-center text-center font-body", {
	defaultVariants: { size: "default" },
	variants: { size: {
		default: "gap-xxs px-xl py-xxl",
		small: "gap-xs px-sm py-sm"
	} }
});
const EmptyStateContext = React.createContext({
	size: "default",
	variant: "default"
});
/**
* Curri-styled empty state container for communicating that a view has no
* content, an action completed, or attention is needed.
*
* Compose with `EmptyStateIcon`, `EmptyStateTitle`, `EmptyStateDescription`,
* and `EmptyStateActions` for structured layouts. The `variant` prop
* propagates color theming to child accent elements via React context.
*
* @param props - Container props with optional color variant
* @returns The rendered empty state container
*/
function EmptyState({ className, size = "default", variant = "default", children, ...props }) {
	return /* @__PURE__ */ jsx(EmptyStateContext.Provider, {
		value: {
			size,
			variant
		},
		children: /* @__PURE__ */ jsx("div", {
			"data-slot": "empty-state",
			className: cn(emptyStateRootVariants({ size }), className),
			...props,
			children
		})
	});
}
const emptyStateIconVariants = cva("flex items-center justify-center rounded-full shrink-0 [&>svg]:shrink-0", {
	defaultVariants: {
		size: "default",
		variant: "default"
	},
	variants: {
		size: {
			default: "mb-xs size-xxl [&>svg]:size-lg",
			small: "mb-xxs size-[36px] [&>svg]:size-[18px]"
		},
		variant: {
			brand: "bg-background-brand-subtle text-icon-brand",
			default: "bg-background-neutral-primary border-(length:--border-width-default) border-border-primary text-icon-secondary shadow-elevation-element",
			destructive: "bg-background-system-danger text-icon-danger",
			information: "bg-background-system-information text-icon-information",
			success: "bg-background-system-success text-icon-success",
			warning: "bg-background-system-warning text-icon-warning"
		}
	}
});
/**
* Circular icon container that renders children (typically a Phosphor icon)
* inside a variant-colored background circle.
*
* Automatically inherits the color variant from the parent `EmptyState`.
* SVG children are sized to 24px by default via descendant selector.
*
* @param props - Standard div props; pass an icon element as children
* @returns The rendered icon container
*/
function EmptyStateIcon({ className, children, ...props }) {
	const { size, variant } = React.useContext(EmptyStateContext);
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "empty-state-icon",
		className: cn(emptyStateIconVariants({
			size,
			variant
		}), className),
		...props,
		children
	});
}
const emptyStateTitleVariants = cva("m-0 self-stretch text-text-secondary", {
	defaultVariants: { size: "default" },
	variants: { size: {
		default: "text-heading-small leading-heading-small font-heading-semi-bold",
		small: "text-body-small leading-body-small font-heading-semi-bold"
	} }
});
/**
* Heading element for the empty state, styled with the heading/small
* treatment (16 px, semi-bold).
*
* Renders an `<h3>` with browser default margin reset.
*
* @param props - Standard heading props
* @returns The rendered empty state title
*/
function EmptyStateTitle({ className, ...props }) {
	const { size } = React.useContext(EmptyStateContext);
	return /* @__PURE__ */ jsx("h3", {
		"data-slot": "empty-state-title",
		className: cn(emptyStateTitleVariants({ size }), className),
		...props
	});
}
const emptyStateDescriptionVariants = cva("m-0 text-center text-text-tertiary", {
	defaultVariants: { size: "default" },
	variants: { size: {
		default: "max-w-[360px] text-body-medium leading-body-medium font-heading-medium",
		small: "max-w-[220px] text-body-xsmall leading-body-xsmall font-heading-medium"
	} }
});
/**
* Secondary body text providing context or instructions in the empty state.
*
* Renders a `<p>` with browser default margin reset. Stretches to the
* parent width so text wraps naturally in centered flex layouts.
*
* @param props - Standard paragraph props
* @returns The rendered empty state description
*/
function EmptyStateDescription({ className, ...props }) {
	const { size } = React.useContext(EmptyStateContext);
	return /* @__PURE__ */ jsx("p", {
		"data-slot": "empty-state-description",
		className: cn(emptyStateDescriptionVariants({ size }), className),
		...props
	});
}
const emptyStateActionsVariants = cva("flex items-center gap-sm", {
	defaultVariants: { size: "default" },
	variants: { size: {
		default: "mt-sm",
		small: "mt-xs"
	} }
});
/**
* Horizontal action area for buttons or links below the empty state message.
*
* @param props - Standard div props; pass Button components as children
* @returns The rendered action container
*/
function EmptyStateActions({ className, ...props }) {
	const { size } = React.useContext(EmptyStateContext);
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "empty-state-actions",
		className: cn(emptyStateActionsVariants({ size }), className),
		...props
	});
}
//#endregion
export { EmptyState, EmptyStateActions, EmptyStateDescription, EmptyStateIcon, EmptyStateTitle, emptyStateIconVariants };

//# sourceMappingURL=EmptyState.js.map